import Grid from '@mui/material/Grid2';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { type Theme } from '@mui/material/styles';
import { useRootContainerContext } from '../../context/root-container';
import { useSettingsContext } from '../../context/settings';
import { useResponsive } from '../../hooks/use-responsive';
import useLocales from '../../hooks/useLocales';
import formatText from '../../utils/texte/formatText';
import checkUndefinedNullOrEmpty from '../../utils/tools/checkUndefinedNullOrEmpty';
import getStyleQuery from '../../utils/tools/getStyleQuery';
import RHFCheckbox from '../hook-form/rhf-checkbox/component';
import RHFDateTimePicker from '../hook-form/rhf-datetimepicker/component';
import RHFTextField from '../hook-form/rhf-text-field/component';
import Iconify from '../iconify/component';
import type { ContactVendorProps } from './types';

// const Grid = lazy(() => import('@mui/material/Grid2' /* webpackChunkName: "Mui_Grid" */));
// const IconButton = lazy(() => import('@mui/material/IconButton' /* webpackChunkName: "Mui_IconButton" */));
// const InputAdornment = lazy(() => import('@mui/material/InputAdornment' /* webpackChunkName: "Mui_InputAdornment" */));
// const Link = lazy(() => import('@mui/material/Link' /* webpackChunkName: "Mui_Link" */));
// const Typography = lazy(() => import('@mui/material/Typography' /* webpackChunkName: "Mui_Typography" */));

export default function ContactVendor({ isProbefahrt = false, setValue, values }: ContactVendorProps) {
    const { translate } = useLocales();
    const settings = useSettingsContext();
    const rootContainer = useRootContainerContext();
    const xs = useResponsive(rootContainer, 'down', 'sm');

    return (
        <>
            <Grid container spacing={2}>
                {/* Name */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <RHFTextField
                        ariaLabel='name'
                        autoComplete="name"
                        label={translate('common:carView.contact.name')}
                        name="name"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="mdi:account" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Mail */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <RHFTextField
                        ariaLabel='email'
                        autoComplete="email"
                        type="email"
                        label={translate('common:carView.contact.mail')}
                        name="email"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="mdi:email" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Telefon */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <RHFTextField
                        ariaLabel='tel'
                        autoComplete="tel"
                        type="tel"
                        label={translate('common:carView.contact.phone')}
                        name="tel"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="mdi:phone" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Probefahrt in Probefahrt Anfrage */}
                {[20].includes(values?.event) && (
                    <Grid size={{ xs: 12, md: 6 }}>
                        <RHFDateTimePicker
                            format="dd.MM.yyyy HH:mm"
                            label={translate('common:carView.contact.date')}
                            name="termin"
                            slotProps={{
                                textField: {
                                    InputProps: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Iconify icon="mdi:calendar-today" />
                                            </InputAdornment>
                                        ),
                                    },
                                },
                            }}
                        />
                    </Grid>
                )}

                {/* Message */}
                <Grid size={{ xs: 12 }}>
                    <RHFTextField
                        ariaLabel='Nachricht'
                        autoComplete="off"
                        multiline
                        id="RHFTextfieldMessage"
                        label={translate('common:carView.contact.message')}
                        name="message"
                        minRows="3"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="mdi:chat" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Ansprechpartner */}
                {!checkUndefinedNullOrEmpty(values?.ansprechpartner) && values?.ansprechpartner > 0 && (
                    <Grid size={{ xs: 12 }}>
                        <RHFTextField
                            ariaLabel='Ansprechpartner'
                            autoComplete="off"
                            disabled
                            label={translate('common:carView.contact.contactPerson')}
                            name="ansprechpartner"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={function () {
                                                setValue('ansprechpartner', null);
                                            }}
                                        >
                                            <Iconify icon="mdi:close" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="mdi:clipboard-account" />
                                    </InputAdornment>
                                ),
                            }}
                            value={values?.car?.usersSelect?.find((u) => u?.i === values?.ansprechpartner)?.t}
                        />
                    </Grid>
                )}

                {/* Finanzdienstleistung */}
                {!checkUndefinedNullOrEmpty(values?.fd) && (
                    <Grid size={{ xs: 12 }}>
                        <RHFTextField
                            multiline
                            autoComplete="off"
                            ariaLabel='Finanzierung'
                            disabled
                            label={translate('common:carView.contact.fd')}
                            name="fd"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={function () {
                                                setValue('fd', null);
                                            }}
                                        >
                                            <Iconify icon="mdi:close" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="mdi:cash-100" />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                sx: {
                                    [getStyleQuery()]: {
                                        fontSize: (theme: Theme) => theme.rawTypography.caption.fontSize,
                                        overflow: 'visible !important', // Enables scrolling for textarea
                                    },
                                },
                            }}
                            value={
                                (values?.accessoiresPrice > 0 ? values?.fd?.disclaimer?.summary?.concat(`, Zugzüglich Zubehör: ${values?.accessoiresPrice} €'`) : values?.fd?.disclaimer?.summary) || ''
                            }
                        />
                    </Grid>
                )}

                {/* Zubehör */}
                {!checkUndefinedNullOrEmpty(values?.accessoires) && !isProbefahrt && (
                    <Grid size={{ xs: 12 }}>
                        <RHFTextField
                            multiline
                            ariaLabel='Zubehör'
                            autoComplete="off"
                            disabled
                            label={translate('common:carView.accessoires.titleSelected')}
                            name="accessoires"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={function () {
                                                setValue('accessoires', '');
                                            }}
                                        >
                                            <Iconify icon="mdi:close" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="mdi:basket-plus" />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                sx: {
                                    [getStyleQuery()]: {
                                        fontSize: (theme: Theme) => theme.typography.caption.fontSize,
                                        overflow: 'visible !important', // Enables scrolling for textarea
                                    },
                                },
                            }}
                            value={values?.accessoires || ''}
                        />
                    </Grid>
                )}

                {/* Test Drive desired in calculator contact form */}
                {!isProbefahrt && values?.event === 0 && (
                    <Grid container size={{ xs: 12 }}>
                        <Grid size={{ xs: 12, sm: 4 }} mb={xs ? 2 : 0}>
                            <RHFCheckbox
                                id="terminWithContactRequest"
                                label={translate('common:carView.contact.testDriveWithContactRequest')}
                                name="terminWithContactRequest"
                            />
                        </Grid>
                        {/* Probefahrt in Kontakt/FinLease Anfrage */}
                        {values?.terminWithContactRequest === true && (
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <RHFDateTimePicker
                                    format="dd.MM.yyyy HH:mm"
                                    label={translate('common:carView.contact.dateWithContactRequest')}
                                    name="termin"
                                    slotProps={{
                                        textField: {
                                            InputProps: {
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Iconify icon="mdi:calendar-today" />
                                                    </InputAdornment>
                                                ),
                                            },
                                        },
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}

                {/* Privacy Disclaimer */}
                <Grid size={{ xs: 12 }}>
                    {!checkUndefinedNullOrEmpty(settings?.contact?.useTextInsteadOfPrivacyCheckBox) ? (
                        <Typography variant="caption" color="text.secondary">
                            {settings?.contact?.useTextInsteadOfPrivacyCheckBox}{' '}
                            <Link href={settings?.contact?.linkDisclaimer} style={{ fontSize: '1em' }} target="_blank" rel="noreferrer">
                                {translate('common:carView.contact.disclaimerLink')}
                            </Link>
                        </Typography>
                    ) : (
                        <RHFCheckbox
                            id="acceptDisclaimer"
                            label={formatText(
                                translate('common:carView.contact.disclaimer'),
                                <Link href={settings?.contact?.linkDisclaimer} style={{ fontSize: '1em' }} target="_blank" rel="noreferrer">
                                    {translate('common:carView.contact.disclaimerLink')}
                                </Link>
                            )}
                            name="acceptDisclaimer"
                        />
                    )}
                </Grid>

                {/* Rückruf/Call back */}
                <Grid size={{ xs: 12 }}>
                    <RHFCheckbox
                        id="callMeBack"
                        label="Call me back"
                        name="callMeBack"
                        tabIndex={-1}
                        sx={{ [getStyleQuery()]: { display: 'none' } }}
                    />
                </Grid>
            </Grid>
        </>
    );
}
